import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Building2, Users, ShieldCheck, Upload, Check, Plus, Trash2, CheckCircle2, Landmark } from "lucide-react";
import logo from "@/assets/murphy-logo.png";

export default function Portal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/portal/${token}/info`).then((r) => { setInfo(r.data); setVerified(r.data.verified); }).catch((e) => setError(apiError(e)));
  }, [token]);

  const loadData = useCallback(() => api.get(`/portal/${token}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e))), [token]);
  useEffect(() => { if (verified) loadData(); }, [verified, loadData]);

  const verify = async () => {
    try { await api.post(`/portal/${token}/verify`, { code }); setVerified(true); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <Shell><p className="text-destructive text-center">{error}</p></Shell>;
  if (!info) return <Shell><p className="text-muted-foreground text-center">Laster…</p></Shell>;

  if (!verified) {
    return (
      <Shell>
        <div className="text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-white mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-2xl">Verifiser deg</h1>
          <p className="text-muted-foreground text-sm mt-1">Vi har sendt en engangskode til {info.email_hint}</p>
          <Input data-testid="portal-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-sifret kode"
            className="mt-6 bg-secondary border-border text-center text-lg tracking-widest max-w-xs mx-auto" />
          <Button data-testid="portal-verify" onClick={verify} className="mt-4 bg-white text-black hover:bg-white/90 w-full max-w-xs">Bekreft</Button>
        </div>
      </Shell>
    );
  }

  if (!data) return <Shell><p className="text-muted-foreground text-center">Laster…</p></Shell>;
  if (data.locked) return <Shell><div className="text-center"><CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-4" /><h1 className="font-display text-2xl">Takk!</h1><p className="text-muted-foreground mt-2">Vi har mottatt din KYC og gjennomgår informasjonen. Du hører fra oss snart.</p></div></Shell>;

  return <PortalForm token={token} data={data} reload={loadData} />;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen noise-bg flex items-center justify-center p-4">
      <div className="w-full max-w-2xl relative z-10">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <img src={logo} alt="Murphy" className="h-7 w-auto brightness-0 invert" />
          <span className="text-muted-foreground text-sm">Kundeportal</span>
        </div>
        <div className="bg-card border border-border rounded-xl p-8">{children}</div>
      </div>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

function PortalForm({ token, data, reload }) {
  const [answers, setAnswers] = useState(data.kyc.answers || {});
  const [board, setBoard] = useState(
    data.kyc.board_members?.length ? data.kyc.board_members : (data.board_source || [])
  );
  const [owners, setOwners] = useState(data.kyc.owners?.length ? data.kyc.owners : []);
  const [beneficial, setBeneficial] = useState(data.kyc.beneficial_owners?.length ? data.kyc.beneficial_owners : []);
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved | error
  const sections = data.questions.reduce((acc, q) => { (acc[q.section] = acc[q.section] || []).push(q); return acc; }, {});

  const requiredDone = data.questions.filter((q) => q.required).every((q) => answers[q.id] != null && answers[q.id] !== "");
  const progress = Math.min(100, (Object.keys(answers).length / Math.max(1, data.questions.length)) * 60 + (owners.length ? 20 : 0) + (data.documents.length ? 20 : 0));

  // Autolagring: lagrer stille 1,5 s etter at kunden slutter å skrive (hopper over første render)
  const firstRun = useRef(true);
  const saveTimer = useRef(null);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setAutoState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/portal/${token}/kyc`, { answers, owners, beneficial_owners: beneficial, board_members: board });
        setAutoState("saved");
      } catch { setAutoState("error"); }
    }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [answers, owners, beneficial, board, token]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/portal/${token}/kyc`, { answers, owners, beneficial_owners: beneficial, board_members: board }); setAutoState("saved"); toast.success("Lagret"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const upload = async (e, cat) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", cat);
    await api.post(`/portal/${token}/documents`, fd); toast.success("Lastet opp"); reload();
  };
  const submit = async () => {
    if (!requiredDone) { toast.error("Fyll ut alle påkrevde felter"); return; }
    await save();
    try { await api.post(`/portal/${token}/submit`); toast.success("Sendt inn!"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen noise-bg py-8 px-4">
      <Toaster position="bottom-right" theme="dark" />
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <img src={logo} alt="Murphy" className="h-7 w-auto brightness-0 invert" />
          <div><span className="text-muted-foreground text-sm">Kundeportal</span><p className="text-xs text-muted-foreground">{data.customer.legal_name}</p></div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 mb-4">
          <div className="flex justify-between items-center text-sm mb-2">
            <span>Fremdrift i onboarding</span>
            <span className="flex items-center gap-3">
              <AutoSaveBadge state={autoState} />
              <span className="text-muted-foreground">{Math.round(progress)}%</span>
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Section icon={Building2} title="1. Bekreft selskapsinformasjon">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="label-caps">Navn</p><p>{data.customer.legal_name}</p></div>
            <div><p className="label-caps">Org.nr</p><p>{data.customer.org_number || "—"}</p></div>
            <div><p className="label-caps">Selskapsform</p><p>{data.customer.company_form || "—"}</p></div>
            <div><p className="label-caps">Adresse</p><p>{data.customer.address || "—"}</p></div>
          </div>
        </Section>

        <Section icon={Landmark} title="2. Styre og roller (hentet fra Brønnøysund)">
          <p className="text-sm text-muted-foreground mb-3">Vi har hentet styre, daglig leder og signaturberettigede automatisk fra Brønnøysundregistrene. Kontroller at listen stemmer og legg til fødselsnummer der det mangler (brukes til identitetskontroll / AML).</p>
          <BoardList items={board} setItems={setBoard} />
        </Section>

        <Section icon={Users} title="3. Eiere og reelle rettighetshavere">
          <PersonList label="Eier" items={owners} setItems={setOwners} testid="owner" />
          <div className="mt-4"><PersonList label="Reell rettighetshaver" items={beneficial} setItems={setBeneficial} pep testid="beneficial" /></div>
        </Section>

        <Section icon={ShieldCheck} title="4. KYC-spørsmål">
          {Object.entries(sections).map(([sec, qs]) => (
            <div key={sec} className="mb-4">
              <p className="label-caps mb-2">{sec}</p>
              <div className="space-y-3">
                {qs.map((q) => (
                  <div key={q.id}>
                    <Label className="text-sm">{q.label} {q.required && <span className="text-destructive">*</span>}</Label>
                    {q.type === "textarea" ? (
                      <Textarea data-testid={`q-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} className="mt-1 bg-secondary border-border" rows={2} />
                    ) : q.type === "select" ? (
                      <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                        <SelectTrigger data-testid={`q-${q.id}`} className="mt-1 bg-secondary border-border"><SelectValue placeholder="Velg" /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={q.type === "number" ? "number" : "text"} data-testid={`q-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} className="mt-1 bg-secondary border-border" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        <Section icon={Upload} title="5. Last opp dokumentasjon">
          <div className="flex flex-wrap gap-2">
            {[["legitimasjon", "Legitimasjon"], ["selskapsdokumentasjon", "Firmaattest"], ["eierstruktur", "Eierstruktur"], ["kyc", "Annet"]].map(([cat, label]) => (
              <Button key={cat} variant="secondary" size="sm" asChild><label data-testid={`upload-${cat}`}><Upload className="w-4 h-4 mr-1" /> {label}<input type="file" className="hidden" onChange={(e) => upload(e, cat)} /></label></Button>
            ))}
          </div>
          <div className="mt-3 space-y-1">
            {data.documents.map((d) => <p key={d.id} className="text-sm text-muted-foreground flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {d.name}</p>)}
          </div>
        </Section>

        <div className="mt-6">
          <p className="text-xs text-muted-foreground text-right mb-2">Svarene dine lagres automatisk. Du kan trygt lukke siden og fortsette senere via samme lenke.</p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" data-testid="portal-save" onClick={save} disabled={saving}>Lagre nå</Button>
            <Button data-testid="portal-submit" onClick={submit} className="bg-white text-black hover:bg-white/90"><Check className="w-4 h-4 mr-1" /> Send inn</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutoSaveBadge({ state }) {
  if (state === "saving") return <span data-testid="autosave-status" className="text-xs text-muted-foreground">Lagrer…</span>;
  if (state === "saved") return <span data-testid="autosave-status" className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Lagret automatisk</span>;
  if (state === "error") return <span data-testid="autosave-status" className="text-xs text-destructive">Autolagring feilet – bruk «Lagre nå»</span>;
  return null;
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-4">
      <div className="flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} /><h2 className="font-display text-lg">{title}</h2></div>
      {children}
    </div>
  );
}

function validSsn(v) { return /^\d{11}$/.test((v || "").trim()); }

function BoardList({ items, setItems }) {
  const add = () => setItems([...items, { _key: (crypto.randomUUID?.() || String(Date.now() + Math.random())), name: "", role: "", ssn: "", birth_date: "", source: "manuell" }]);
  const upd = (i, k, v) => { const cp = [...items]; cp[i] = { ...cp[i], [k]: v }; setItems(cp); };
  const del = (i) => setItems(items.filter((_, x) => x !== i));
  return (
    <div>
      <div className="space-y-3">
        {items.map((o, i) => {
          const ssnBad = o.ssn && !validSsn(o.ssn);
          return (
            <div key={o._key || `board-${i}`} className="rounded-lg border border-border p-3 space-y-2 relative" data-testid={`board-row-${i}`}>
              <button onClick={() => del(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive" data-testid={`del-board-${i}`}><Trash2 className="w-4 h-4" /></button>
              <div className="grid grid-cols-12 gap-2 pr-6">
                <Input placeholder="Fullt navn" data-testid={`board-name-${i}`} value={o.name || ""} onChange={(e) => upd(i, "name", e.target.value)} className="col-span-7 bg-secondary border-border h-9" />
                <Input placeholder="Rolle" data-testid={`board-role-${i}`} value={o.role || ""} onChange={(e) => upd(i, "role", e.target.value)} className="col-span-5 bg-secondary border-border h-9" />
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-7">
                  <Input placeholder="Fødselsnummer (11 siffer)" data-testid={`board-ssn-${i}`} value={o.ssn || ""} maxLength={11} inputMode="numeric" onChange={(e) => upd(i, "ssn", e.target.value.replace(/\D/g, ""))} className={`bg-secondary border-border h-9 ${ssnBad ? "border-destructive" : ""}`} />
                  {ssnBad && <p className="text-xs text-destructive mt-1">Fødselsnummer må være 11 siffer</p>}
                </div>
                <p className="col-span-5 text-xs text-muted-foreground">
                  {o.birth_date ? `Født ${o.birth_date}` : ""}{o.source === "brreg" ? " · Brønnøysund" : ""}
                </p>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Ingen roller hentet. Legg til manuelt ved behov.</p>}
      </div>
      <Button size="sm" variant="secondary" onClick={add} data-testid="add-board" className="mt-3"><Plus className="w-4 h-4 mr-1" /> Legg til person</Button>
    </div>
  );
}

function PersonList({ label, items, setItems, pep, testid }) {
  const add = () => setItems([...items, { _key: (crypto.randomUUID?.() || String(Date.now() + Math.random())), name: "", ssn: "", org_number: "", is_company: false, share: "", role: "", pep: false }]);
  const upd = (i, k, v) => { const cp = [...items]; cp[i] = { ...cp[i], [k]: v }; setItems(cp); };
  const del = (i) => setItems(items.filter((_, x) => x !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><p className="text-sm font-medium">{label}e</p><Button size="sm" variant="secondary" onClick={add} data-testid={`add-${testid}`}><Plus className="w-4 h-4 mr-1" /> Legg til</Button></div>
      <div className="space-y-3">
        {items.map((o, i) => {
          const isCo = !!o.is_company;
          const ssnBad = !isCo && o.ssn && !validSsn(o.ssn);
          const orgBad = isCo && o.org_number && !/^\d{9}$/.test(String(o.org_number).replace(/\D/g, ""));
          return (
            <div key={o._key || o.id || `${testid}-${i}`} className="rounded-lg border border-border p-3 space-y-2 relative" data-testid={`${testid}-row-${i}`}>
              <button onClick={() => del(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive" data-testid={`del-${testid}-${i}`}><Trash2 className="w-4 h-4" /></button>
              <div className="flex gap-1 pr-6">
                <button type="button" data-testid={`${testid}-type-person-${i}`} onClick={() => upd(i, "is_company", false)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${!isCo ? "bg-white text-black border-white" : "bg-secondary border-border text-muted-foreground"}`}>Person</button>
                <button type="button" data-testid={`${testid}-type-company-${i}`} onClick={() => upd(i, "is_company", true)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${isCo ? "bg-white text-black border-white" : "bg-secondary border-border text-muted-foreground"}`}>Selskap</button>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <Input placeholder={isCo ? "Selskapsnavn" : "Fullt navn"} data-testid={`${testid}-name-${i}`} value={o.name || ""} onChange={(e) => upd(i, "name", e.target.value)} className="col-span-7 bg-secondary border-border h-9" />
                <div className="col-span-5">
                  {isCo ? (
                    <>
                      <Input placeholder="Organisasjonsnummer (9 siffer)" data-testid={`${testid}-org-${i}`} value={o.org_number || ""} maxLength={9} inputMode="numeric" onChange={(e) => upd(i, "org_number", e.target.value.replace(/\D/g, ""))} className={`bg-secondary border-border h-9 ${orgBad ? "border-destructive" : ""}`} />
                      {orgBad && <p className="text-xs text-destructive mt-1">Organisasjonsnummer må være 9 siffer</p>}
                    </>
                  ) : (
                    <>
                      <Input placeholder="Fødselsnummer (11 siffer)" data-testid={`${testid}-ssn-${i}`} value={o.ssn || ""} maxLength={11} inputMode="numeric" onChange={(e) => upd(i, "ssn", e.target.value.replace(/\D/g, ""))} className={`bg-secondary border-border h-9 ${ssnBad ? "border-destructive" : ""}`} />
                      {ssnBad && <p className="text-xs text-destructive mt-1">Fødselsnummer må være 11 siffer</p>}
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input placeholder="Eierandel %" data-testid={`${testid}-share-${i}`} value={o.share || ""} onChange={(e) => upd(i, "share", e.target.value)} className="col-span-4 bg-secondary border-border h-9" />
                <Input placeholder="Rolle" value={o.role || ""} onChange={(e) => upd(i, "role", e.target.value)} className="col-span-6 bg-secondary border-border h-9" />
                {pep && !isCo ? <label className="col-span-2 flex items-center justify-center gap-1 text-xs"><Checkbox checked={o.pep} onCheckedChange={(v) => upd(i, "pep", v)} data-testid={`${testid}-pep-${i}`} /> PEP</label> : <span className="col-span-2" />}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Ingen registrert.</p>}
        {pep && items.length > 0 && <p className="text-xs text-muted-foreground">Velg «Selskap» for eiere som er foretak (org.nr), eller «Person» (fødselsnummer). Kryss av for PEP (politisk eksponert person) på personer.</p>}
      </div>
    </div>
  );
}
